import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Activity,
    Info,
    KeyRound,
    MailCheck,
    ServerCog,
    ShieldCheck,
    Users,
} from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import ConnectionAccountCard from "../components/connections/ConnectionAccountCard";
import PageContainer from "../components/layout/PageContainer";
import ConnectionMethodCard from "../components/connections/ConnectionMethodCard";
import ConnectionSummaryCard from "../components/connections/ConnectionSummaryCard";
import SmtpConnectionForm from "../components/connections/SmtpConnectionForm";
import {
    createSmtpConnection,
    deleteConnection,
    fetchConnections,
    setPrimaryConnection,
    startGoogleOAuth,
    testConnection,
    updateConnectionPriority,
} from "../store/connectionsSlice";
import { getConnectionDeleteBlockReason } from "../utils/connectionDeleteGuard";

const GOOGLE_CONNECTION_SUCCESS = "GOOGLE_CONNECTION_SUCCESS";
const GOOGLE_CONNECTION_FAILED = "GOOGLE_CONNECTION_FAILED";

const getGoogleOAuthErrorMessage = (error) => {
    const message =
        typeof error === "string"
            ? error
            : error?.message || "Failed to start Google connection.";
    const normalizedMessage = message.toLowerCase();

    if (
        normalizedMessage.includes("google_oauth_not_configured") ||
        normalizedMessage.includes("google oauth2 not configured")
    ) {
        return "Google integration is being set up. Please try again later.";
    }

    return message || "Failed to start Google connection. Please try again.";
};

const getConnectionId = (connection) =>
    String(
        connection?.id ||
            connection?.connection_id ||
            connection?.connectionId ||
            "",
    ).trim();

const getConnectionActionErrorMessage = (
    error,
    fallbackMessage = "Something went wrong. Please try again.",
) => {
    if (typeof error === "string" && error.trim()) {
        return error.trim();
    }

    const detail = error?.response?.data?.detail || error?.detail;

    if (typeof detail?.message === "string" && detail.message.trim()) {
        return detail.message.trim();
    }

    if (typeof detail === "string" && detail.trim()) {
        return detail.trim();
    }

    if (typeof error?.response?.data?.message === "string") {
        return error.response.data.message;
    }

    if (typeof error?.message === "string") {
        return error.message;
    }

    return fallbackMessage;
};

const getConnectionTestErrorMessage = (error) => {
    const message = getConnectionActionErrorMessage(
        error,
        "Connection test failed. Please try again.",
    );

    if (/https?:\/\//i.test(message) || message.length > 160) {
        return "Connection test failed. Please try again.";
    }

    return message;
};

const formatConnectionTestStatus = (status) => {
    const normalizedStatus = String(status || "").trim().toLowerCase();

    if (normalizedStatus === "healthy") return "Healthy";
    if (normalizedStatus === "unhealthy") return "Unhealthy";

    return normalizedStatus
        ? normalizedStatus.replace(/^./, (character) =>
              character.toUpperCase(),
          )
        : "Not tested";
};

const getLatestConnectionTestResult = (resultsByConnectionId = {}) => {
    const testResults = Object.values(resultsByConnectionId).filter(Boolean);

    if (testResults.length === 0) return null;

    return testResults.reduce((latestResult, result) => {
        const latestTime = Date.parse(latestResult?.tested_at || "");
        const resultTime = Date.parse(result?.tested_at || "");

        if (!Number.isFinite(latestTime)) return result;
        if (!Number.isFinite(resultTime)) return latestResult;

        return resultTime >= latestTime ? result : latestResult;
    }, testResults[0]);
};

const ConnectionsPage = () => {
    const dispatch = useDispatch();
    const [googleConnectionStatus, setGoogleConnectionStatus] =
        useState("idle");
    const [settingPrimaryConnectionId, setSettingPrimaryConnectionId] =
        useState("");
    const userId = useSelector((state) => state.auth.user?.id);
    const {
        connections,
        connectionsStatus,
        connectionsError,
        createSmtpConnectionStatus,
        setPrimaryStatus,
        updatePriorityStatusByConnectionId = {},
        deleteStatusByConnectionId = {},
        testResultsByConnectionId = {},
        testStatusByConnectionId = {},
        testErrorByConnectionId = {},
    } = useSelector((state) => state.connections);
    const connectionList = useMemo(
        () => (Array.isArray(connections) ? connections : []),
        [connections],
    );
    const isLoadingConnections = connectionsStatus === "loading";
    const totalConnections = connectionList.length;
    const activeSenders = connectionList.filter(
        (connection) => connection?.is_active,
    ).length;
    const primaryConnection = connectionList.find(
        (connection) => connection?.is_active && connection?.is_primary,
    );
    const latestTestResult = getLatestConnectionTestResult(
        testResultsByConnectionId,
    );
    const lastTestStatus = formatConnectionTestStatus(
        latestTestResult?.status,
    );
    const oauthCallbackParams = new URLSearchParams(window.location.search);
    const oauthCallbackStatus = String(
        oauthCallbackParams.get("status") || "",
    )
        .trim()
        .toLowerCase();
    const oauthCallbackConnectionId =
        oauthCallbackParams.get("connection_id") || "";
    const isGoogleOAuthCallback = ["connected", "failed", "error"].includes(
        oauthCallbackStatus,
    );

    const isGoogleConnecting =
        googleConnectionStatus === "starting" ||
        googleConnectionStatus === "waiting";
    const isCreatingSmtpConnection =
        createSmtpConnectionStatus === "loading";

    const loadConnections = useCallback(async () => {
        try {
            return await dispatch(fetchConnections()).unwrap();
        } catch {
            return [];
        }
    }, [dispatch]);

    const checkConnectionHealthForList = useCallback(async (connections = []) => {
        const connectionsToCheck = Array.isArray(connections)
            ? connections
            : [];

        await Promise.allSettled(
            connectionsToCheck
                .map((connection) => getConnectionId(connection))
                .filter(Boolean)
                .map((connectionId) =>
                    dispatch(testConnection(connectionId)).unwrap(),
                ),
        );
    }, [dispatch]);

    useEffect(() => {
        if (isGoogleOAuthCallback) return;

        let isCancelled = false;

        const loadInitialConnections = async () => {
            const fetchedConnections = await loadConnections();

            if (isCancelled) return;

            await checkConnectionHealthForList(fetchedConnections);
        };

        loadInitialConnections();

        return () => {
            isCancelled = true;
        };
    }, [checkConnectionHealthForList, isGoogleOAuthCallback, loadConnections]);

    useEffect(() => {
        if (!isGoogleOAuthCallback) return;

        const isConnected = oauthCallbackStatus === "connected";
        const message = isConnected
            ? {
                  type: GOOGLE_CONNECTION_SUCCESS,
                  connectionId: oauthCallbackConnectionId,
              }
            : { type: GOOGLE_CONNECTION_FAILED };

        if (window.opener && !window.opener.closed) {
            window.opener.postMessage(message, window.location.origin);
            window.close();
        }
    }, [
        isGoogleOAuthCallback,
        oauthCallbackConnectionId,
        oauthCallbackStatus,
    ]);

    useEffect(() => {
        const handleGoogleOAuthMessage = (event) => {
            if (event.origin !== window.location.origin) return;

            if (event.data?.type === GOOGLE_CONNECTION_SUCCESS) {
                setGoogleConnectionStatus("idle");
                toast.success("Google account connected successfully.");
                loadConnections();
                return;
            }

            if (event.data?.type === GOOGLE_CONNECTION_FAILED) {
                setGoogleConnectionStatus("idle");
                toast.error("Google connection was not completed.");
            }
        };

        window.addEventListener("message", handleGoogleOAuthMessage);

        return () => {
            window.removeEventListener("message", handleGoogleOAuthMessage);
        };
    }, [loadConnections]);

    const handleConnectGoogle = async () => {
        if (isGoogleConnecting) return;

        if (!userId) {
            toast.error("Please sign in again before connecting Google.");
            return;
        }

        setGoogleConnectionStatus("starting");

        try {
            const response = await dispatch(startGoogleOAuth(userId)).unwrap();
            const url = response?.url;

            if (!url) {
                setGoogleConnectionStatus("idle");
                toast.error(
                    "Unable to start Google connection. Please try again.",
                );
                return;
            }

            const popup = window.open(
                url,
                "google-oauth",
                "width=600,height=700",
            );

            if (!popup) {
                setGoogleConnectionStatus("idle");
                window.location.href = url;
                return;
            }

            setGoogleConnectionStatus("waiting");
        } catch (error) {
            setGoogleConnectionStatus("idle");
            toast.error(getGoogleOAuthErrorMessage(error));
        }
    };

    const handleCreateSmtpConnection = async (formData) => {
        if (isCreatingSmtpConnection) return false;

        try {
            await dispatch(createSmtpConnection(formData)).unwrap();
            toast.success("SMTP connection added successfully.");
            loadConnections();
            return true;
        } catch {
            toast.error(
                "Failed to add SMTP connection. Please check your SMTP details.",
            );
            return false;
        }
    };

    const handleTestConnection = useCallback(
        async (connection) => {
            const connectionId = getConnectionId(connection);

            if (!connectionId) {
                toast.error("Connection ID is required.");
                return;
            }

            if (testStatusByConnectionId[connectionId] === "loading") return;

            try {
                const payload = await dispatch(
                    testConnection(connectionId),
                ).unwrap();
                const result = payload?.result || {};
                const status = String(result.status || "")
                    .trim()
                    .toLowerCase();

                if (status === "healthy") {
                    toast.success(
                        result.account_identifier
                            ? `Connection is healthy for ${result.account_identifier}.`
                            : "Connection is healthy.",
                    );
                    return;
                }

                if (status === "unhealthy") {
                    toast.error("Connection test failed. Please try again.");
                    return;
                }

                toast.info("Connection test completed.");
            } catch (error) {
                toast.error(getConnectionTestErrorMessage(error));
            }
        },
        [dispatch, testStatusByConnectionId],
    );

    const handleSetPrimaryConnection = useCallback(
        async (connection) => {
            const connectionId = getConnectionId(connection);

            if (!connectionId) {
                toast.error("Connection ID is required.");
                return;
            }

            if (connection?.is_active === false) {
                toast.error("Inactive connections cannot be set as primary.");
                return;
            }

            if (setPrimaryStatus === "loading") return;

            setSettingPrimaryConnectionId(connectionId);

            try {
                const payload = await dispatch(
                    setPrimaryConnection(connectionId),
                ).unwrap();
                const updatedConnection = payload?.connection || {};
                const accountIdentifier =
                    updatedConnection.account_identifier ||
                    connection.account_identifier;

                toast.success(
                    accountIdentifier
                        ? `${accountIdentifier} is now your primary sender.`
                        : "Primary sender updated successfully.",
                );
                loadConnections();
            } catch (error) {
                const message = getConnectionActionErrorMessage(
                    error,
                    "Failed to set primary connection. Please try again.",
                );

                toast.error(message);
            } finally {
                setSettingPrimaryConnectionId("");
            }
        },
        [dispatch, loadConnections, setPrimaryStatus],
    );

    const handleUpdateConnectionPriority = useCallback(
        async (connection, priority) => {
            const connectionId = getConnectionId(connection);

            if (!connectionId) {
                toast.error("Connection ID is required.");
                return;
            }

            if (
                updatePriorityStatusByConnectionId[connectionId] === "loading"
            ) {
                return;
            }

            try {
                await dispatch(
                    updateConnectionPriority({
                        connectionId,
                        priority,
                    }),
                ).unwrap();

                toast.success("Priority updated successfully.");
                loadConnections();
            } catch (error) {
                const message = getConnectionActionErrorMessage(
                    error,
                    "Failed to update priority. Please try again.",
                );

                toast.error(message);
            }
        },
        [dispatch, loadConnections, updatePriorityStatusByConnectionId],
    );

    const handleDeleteConnection = useCallback(
        async (connection) => {
            const connectionId = getConnectionId(connection);

            if (!connectionId) {
                toast.error("Connection ID is required.");
                return;
            }

            const blockReason = getConnectionDeleteBlockReason(
                connectionList,
                connection,
            );

            if (blockReason) {
                toast.error(blockReason);
                return;
            }

            if (deleteStatusByConnectionId[connectionId] === "loading") return;

            try {
                await dispatch(deleteConnection(connectionId)).unwrap();
                toast.success("Connection deleted successfully.");
                loadConnections();
            } catch (error) {
                const message = getConnectionActionErrorMessage(
                    error,
                    "Failed to delete connection. Please try again.",
                );

                toast.error(message);
            }
        },
        [
            connectionList,
            deleteStatusByConnectionId,
            dispatch,
            loadConnections,
        ],
    );

    if (isGoogleOAuthCallback) {
        const isConnected = oauthCallbackStatus === "connected";

        return (
            <main className="flex min-h-screen items-center justify-center bg-[#F8F8FE] p-4">
                <section className="w-full max-w-md rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
                    <h1 className="text-xl font-bold text-slate-900">
                        {isConnected
                            ? "Google account connected successfully."
                            : "Google connection was not completed."}
                    </h1>
                    <p className="mt-3 text-sm leading-6 text-slate-600">
                        {isConnected
                            ? "You can close this window and return to the app."
                            : "You can close this window and try again."}
                    </p>
                </section>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-[#F8F8FE]">
            <PageContainer className="py-4">
                <header className="mb-5">
                    <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
                        Connections
                    </h1>
                    <p className="mt-2 max-w-2xl text-sm text-slate-600">
                        Manage the email sender accounts used for outreach
                        campaigns.
                    </p>
                </header>

                <div className="mb-4 flex items-start gap-3 px-1 py-1 text-sm text-indigo-700">
                    <Info
                        size={18}
                        className="mt-0.5 shrink-0"
                        aria-hidden="true"
                    />
                    <p className="font-medium">
                        At least one active connection is required before
                        launching a campaign.
                    </p>
                </div>

                <section className="grid grid-cols-2 gap-3 lg:grid-cols-4">
                    <ConnectionSummaryCard
                        icon={MailCheck}
                        title="Total Connections"
                        value={totalConnections}
                        isLoading={isLoadingConnections}
                    />
                    <ConnectionSummaryCard
                        icon={ShieldCheck}
                        title="Primary Sender"
                        value={
                            primaryConnection?.account_identifier || "Not set"
                        }
                        isLoading={isLoadingConnections}
                    />
                    <ConnectionSummaryCard
                        icon={Users}
                        title="Active Senders"
                        value={activeSenders}
                        isLoading={isLoadingConnections}
                    />
                    <ConnectionSummaryCard
                        icon={Activity}
                        title="Last Test Status"
                        value={lastTestStatus}
                        isLoading={isLoadingConnections}
                    />
                </section>

                {connectionsStatus === "failed" && (
                    <div className="mt-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                        <p className="font-semibold">
                            Unable to load connections.
                        </p>
                        {connectionsError && (
                            <p className="mt-1">{connectionsError}</p>
                        )}
                    </div>
                )}

                <div className="mt-5 grid gap-5 xl:grid-cols-[minmax(320px,0.85fr)_minmax(0,1.45fr)]">
                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="border-b border-indigo-100 bg-[#eef2ff] p-4 sm:p-5">
                            <h2 className="text-lg font-semibold text-[#0f172a]">
                                Connection Methods
                            </h2>
                            <p className="mt-1 text-sm text-[#475569]">
                                Choose how sender accounts will connect to
                                outreach.
                            </p>
                        </div>

                        <div className="space-y-3 p-4 sm:p-5">
                            <ConnectionMethodCard
                                icon={KeyRound}
                                title="Google OAuth"
                                description="Connect your Google account to send outreach emails securely."
                                note="Recommended for Gmail and Google Workspace accounts."
                                variant="google"
                                buttonLabel={
                                    googleConnectionStatus === "waiting"
                                        ? "Waiting for Google..."
                                        : googleConnectionStatus === "starting"
                                          ? "Opening Google..."
                                          : "Connect Google"
                                }
                                disabled={isGoogleConnecting}
                                onButtonClick={handleConnectGoogle}
                            />

                            <ConnectionMethodCard
                                icon={ServerCog}
                                title="SMTP"
                                description="Use your SMTP server details for custom email delivery."
                            >
                                <SmtpConnectionForm
                                    onSubmit={handleCreateSmtpConnection}
                                    isSubmitting={isCreatingSmtpConnection}
                                />
                            </ConnectionMethodCard>
                        </div>
                    </section>

                    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
                        <div className="border-b border-indigo-100 bg-[#eef2ff] p-4 sm:p-5">
                            <h2 className="text-lg font-semibold text-[#0f172a]">
                                Connected Accounts
                            </h2>
                            <p className="mt-1 text-sm text-[#475569]">
                                Sender accounts available for outreach
                                campaigns.
                            </p>
                        </div>

                        {connectionsStatus === "loading" && (
                            <div>
                                {[1, 2, 3].map((item) => (
                                    <div
                                        key={item}
                                        className="border-b border-slate-200 px-4 py-3"
                                    >
                                        <div className="flex items-center gap-3">
                                            <div className="h-11 w-11 animate-pulse rounded-2xl bg-slate-100" />
                                            <div className="min-w-0 flex-1">
                                                <div className="h-4 w-2/3 animate-pulse rounded bg-slate-100" />
                                                <div className="mt-3 h-3 w-32 animate-pulse rounded bg-slate-100" />
                                            </div>
                                            <div className="hidden h-8 w-28 animate-pulse rounded bg-slate-100 sm:block" />
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}

                        {connectionsStatus === "failed" && (
                            <div className="m-4 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700">
                                <p className="font-semibold">
                                    Failed to load connected accounts.
                                </p>
                                {connectionsError && (
                                    <p className="mt-1">{connectionsError}</p>
                                )}
                            </div>
                        )}

                        {connectionsStatus !== "loading" &&
                            connectionsStatus !== "failed" &&
                            connectionList.length === 0 && (
                                <div className="m-4 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-8 text-center">
                                    <p className="font-semibold text-slate-900">
                                        No connected accounts yet.
                                    </p>
                                    <p className="mt-1 text-sm text-slate-500">
                                        Connect Google to add your first sender
                                        account.
                                    </p>
                                </div>
                            )}

                        {connectionsStatus !== "loading" &&
                            connectionsStatus !== "failed" &&
                            connectionList.length > 0 && (
                                <div className="product-scrollbar max-h-[710px] overflow-y-auto">
                                    {connectionList.map(
                                        (connection, index) => {
                                            const connectionId =
                                                getConnectionId(connection);

                                            return (
                                                <ConnectionAccountCard
                                                    key={
                                                        connectionId ||
                                                        `${connection.account_identifier || "connection"}-${index}`
                                                    }
                                                    connection={connection}
                                                    onTestConnection={() =>
                                                        handleTestConnection(
                                                            connection,
                                                        )
                                                    }
                                                    onSetPrimaryConnection={() =>
                                                        handleSetPrimaryConnection(
                                                            connection,
                                                        )
                                                    }
                                                    onPriorityChange={(
                                                        priority,
                                                    ) =>
                                                        handleUpdateConnectionPriority(
                                                            connection,
                                                            priority,
                                                        )
                                                    }
                                                    onDeleteConnection={() =>
                                                        handleDeleteConnection(
                                                            connection,
                                                        )
                                                    }
                                                    isSettingPrimary={
                                                        setPrimaryStatus ===
                                                            "loading" &&
                                                        settingPrimaryConnectionId ===
                                                            connectionId
                                                    }
                                                    isDeleting={
                                                        deleteStatusByConnectionId[
                                                            connectionId
                                                        ] === "loading"
                                                    }
                                                    priorityStatus={
                                                        updatePriorityStatusByConnectionId[
                                                            connectionId
                                                        ]
                                                    }
                                                    testStatus={
                                                        testStatusByConnectionId[
                                                            connectionId
                                                        ]
                                                    }
                                                    testResult={
                                                        testResultsByConnectionId[
                                                            connectionId
                                                        ]
                                                    }
                                                    testError={
                                                        testErrorByConnectionId[
                                                            connectionId
                                                        ]
                                                    }
                                                />
                                            );
                                        },
                                    )}
                                </div>
                            )}
                    </section>
                </div>
            </PageContainer>
        </main>
    );
};

export default ConnectionsPage;
